import { createTheme } from "@mui/material";
import type { ThemeMode } from "../theme/tokens";
import { cockpitColorTokens } from "../theme/tokens";

export const createAppTheme = (mode: ThemeMode) => {
  const colors = cockpitColorTokens[mode];

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.blue,
      },
      secondary: {
        main: mode === "dark" ? "#B48CFF" : "#7651C8",
      },
      success: {
        main: colors.green,
      },
      warning: {
        main: colors.amber,
      },
      error: {
        main: colors.red,
      },
      background: {
        default: colors.background,
        paper: colors.panel,
      },
      text: {
        primary: colors.text,
        secondary: colors.muted,
        disabled: colors.faint,
      },
      divider: colors.line,
    },
    typography: {
      fontFamily: '"Barlow", "Helvetica Neue", Arial, sans-serif',
      fontSize: 14,
      button: {
        fontWeight: 600,
        textTransform: "none",
      },
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 700 },
    },
    shape: {
      borderRadius: 9,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colors.background,
            color: colors.text,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: `1px solid ${colors.line}`,
            boxShadow: "var(--shadow)",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            border: `1px solid ${colors.line}`,
            backgroundImage: "none",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: colors.panelRaised,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.lineStrong,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.muted,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: colors.blue,
              borderWidth: 2,
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            maxWidth: "100%",
            overflowX: "auto",
            borderRadius: 9,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: colors.line,
          },
          head: {
            backgroundColor: colors.panelRaised,
            color: colors.text,
            fontWeight: 700,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:nth-of-type(even)": {
              backgroundColor: mode === "dark" ? "rgba(255,255,255,0.018)" : "rgba(20,35,60,0.025)",
            },
            "&:hover": {
              backgroundColor: mode === "dark" ? "rgba(106,166,255,0.08)" : "rgba(46,107,223,0.06)",
            },
          },
        },
      },
      MuiTabs: {
        defaultProps: {
          variant: "scrollable",
          scrollButtons: "auto",
          allowScrollButtonsMobile: true,
        },
        styleOverrides: {
          root: {
            minHeight: 42,
            borderBottom: `1px solid ${colors.line}`,
          },
          scrollButtons: {
            color: colors.muted,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 42,
            minWidth: 0,
            padding: "8px 14px",
            textTransform: "none",
            fontWeight: 600,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            border: `1px solid ${colors.line}`,
            backgroundImage: "none",
            "&:before": { display: "none" },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderColor: colors.lineStrong,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colors.panelHighest,
            color: colors.text,
            border: `1px solid ${colors.lineStrong}`,
            fontSize: 12,
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: false,
        },
      },
    },
  });
};

// Kept for modules that still import the legacy static theme during migration.
export const theme = createAppTheme("light");
